import { useState, useEffect } from 'react'

export class FallbackStorage implements Storage {
  private readonly map = new Map<string, string>()

  getItem(key: string): string | null {
    return this.map.get(key) ?? null
  }

  setItem(key: string, value: string): void {
    this.map.set(key, value)
  }

  removeItem(key: string): void {
    this.map.delete(key)
  }

  clear(): void {
    this.map.clear()
  }

  key(index: number): string | null {
    const keys = Array.from(this.map.keys())
    return keys[index] ?? null
  }

  get length(): number {
    return this.map.size
  }
}

export type StorageType = 'session' | 'local'

const isSupported: Record<StorageType, boolean> = {
  session: true,
  local: true,
}

const getStorage = (getStorage: () => Storage, name: StorageType): Storage => {
  try {
    const storage = getStorage()
    const testKey = 'x-test-key'
    storage.setItem(testKey, '1')
    const one = storage.getItem(testKey)
    storage.removeItem(testKey)
    if (one === '1') {
      return storage
    }
  } catch (e) {}
  isSupported[name] = false
  return new FallbackStorage()
}

const storage = {
  session: getStorage(() => window?.sessionStorage, 'session'),
  local: getStorage(() => window?.localStorage, 'local'),
} as const

export const isStorageSupported = (type: StorageType): boolean => isSupported[type]

export const useLocalStorage = <T>(key: string, initialState: T): [T, (s: T) => void] => {
  const initialValue = JSON.parse(storage.local.getItem(key) ?? JSON.stringify(initialState))
  const [value, setValue] = useState<T>(initialValue)

  useEffect(() => {
    storage.local.setItem(key, JSON.stringify(value))
  }, [value])

  return [value, setValue]
}

export const getLocalStorageItem = <T>(key: string, initialState: T | null = null): T | null => {
  try {
    const rawValue = storage.local.getItem(key)
    if (rawValue == null) {
      return initialState
    }

    return JSON.parse(rawValue) ?? initialState
  } catch (ignore) {
    return initialState
  }
}

export const getSessionStorageItem = <T>(key: string): T | null => {
  try {
    const rawValue = storage.session.getItem(key)
    if (rawValue == null) {
      return null
    }

    return JSON.parse(rawValue)
  } catch (ignore) {
    return null
  }
}

export const setLocalStorageItem = <T>(key: string, value: T): T => {
  storage.local.setItem(key, JSON.stringify(value))
  return value
}

export const setSessionStorageItem = <T>(key: string, value: T): T => {
  storage.session.setItem(key, JSON.stringify(value))
  return value
}

export const removeLocalStorageItem = (key: string): void => {
  storage.local.removeItem(key)
}

export const removeSessionStorageItem = (key: string): void => {
  storage.session.removeItem(key)
}
